import React, { useState } from 'react';
import './Reports.css';

interface ReportData {
  abcAnalysis: Array<{
    category: string;
    items: number;
    percentage: number;
    value: number;
    description: string;
  }>;
  turnoverRate: Array<{
    period: string;
    rate: number;
    target: number;
    status: string;
  }>;
  stockLevel: Array<{
    category: string;
    current: number;
    optimal: number;
    percentage: number;
  }>;
  inventoryHealth: {
    score: number;
    grade: string;
    factors: Array<{
      name: string;
      score: number;
      status: 'excellent' | 'good' | 'warning' | 'danger';
      description: string;
    }>;
  };
  costAnalysis: {
    totalCost: number;
    totalValue: number;
    averageMargin: number;
    categories: Array<{
      name: string;
      cost: number;
      value: number;
      margin: number;
      items: number;
    }>;
  };
  trendAnalysis: Array<{
    month: string;
    totalValue: number;
    totalItems: number;
    lowStock: number;
    outOfStock: number;
  }>;
  topItems: {
    byValue: Array<{
      name: string;
      value: number;
      percentage: number;
    }>;
    byQuantity: Array<{
      name: string;
      quantity: number;
      percentage: number;
    }>;
    byTurnover: Array<{
      name: string;
      turnover: number;
      status: 'excellent' | 'good' | 'warning';
    }>;
  };
}

const mockReportData: ReportData = {
  abcAnalysis: [
    { category: 'A', items: 23, percentage: 15.6, value: 1715000, description: '高価値・高回転' },
    { category: 'B', items: 47, percentage: 32.0, value: 588000, description: '中価値・中回転' },
    { category: 'C', items: 77, percentage: 52.4, value: 147000, description: '低価値・低回転' }
  ],
  turnoverRate: [
    { period: '2024年12月', rate: 2.8, target: 3.0, status: 'below' },
    { period: '2024年11月', rate: 3.2, target: 3.0, status: 'above' },
    { period: '2024年10月', rate: 2.9, target: 3.0, status: 'below' },
    { period: '2024年09月', rate: 3.4, target: 3.0, status: 'above' },
    { period: '2024年08月', rate: 3.1, target: 3.0, status: 'above' },
    { period: '2024年07月', rate: 2.7, target: 3.0, status: 'below' }
  ],
  stockLevel: [
    { category: '原材料', current: 68, optimal: 75, percentage: 90.7 },
    { category: '部品', current: 42, optimal: 50, percentage: 84.0 },
    { category: '完成品', current: 23, optimal: 30, percentage: 76.7 },
    { category: '工具', current: 8, optimal: 10, percentage: 80.0 },
    { category: '消耗品', current: 6, optimal: 8, percentage: 75.0 }
  ],
  inventoryHealth: {
    score: 78.5,
    grade: 'B',
    factors: [
      { name: '在庫回転率', score: 82, status: 'good', description: '目標値に対して良好なパフォーマンス' },
      { name: '在庫充足率', score: 85, status: 'good', description: 'ほとんどのカテゴリで最適在庫を維持' },
      { name: '在庫切れ率', score: 65, status: 'warning', description: '3アイテムが在庫切れ状態' },
      { name: 'デッドストック率', score: 88, status: 'excellent', description: '滞留在庫が非常に少ない' },
      { name: '在庫精度', score: 92, status: 'excellent', description: '棚卸差異が最小限' }
    ]
  },
  costAnalysis: {
    totalCost: 1890000,
    totalValue: 2450000,
    averageMargin: 22.9,
    categories: [
      { name: '原材料', cost: 816000, value: 1020000, margin: 20.0, items: 68 },
      { name: '部品', cost: 252000, value: 336000, margin: 25.0, items: 42 },
      { name: '完成品', cost: 149500, value: 195500, margin: 23.5, items: 23 },
      { name: '工具', cost: 76000, value: 96000, margin: 20.8, items: 8 },
      { name: '消耗品', cost: 15000, value: 19200, margin: 21.9, items: 6 }
    ]
  },
  trendAnalysis: [
    { month: '2024-07', totalValue: 2280000, totalItems: 142, lowStock: 8, outOfStock: 2 },
    { month: '2024-08', totalValue: 2350000, totalItems: 145, lowStock: 10, outOfStock: 1 },
    { month: '2024-09', totalValue: 2420000, totalItems: 148, lowStock: 11, outOfStock: 3 },
    { month: '2024-10', totalValue: 2390000, totalItems: 146, lowStock: 9, outOfStock: 2 },
    { month: '2024-11', totalValue: 2410000, totalItems: 147, lowStock: 12, outOfStock: 3 },
    { month: '2024-12', totalValue: 2450000, totalItems: 147, lowStock: 12, outOfStock: 3 }
  ],
  topItems: {
    byValue: [
      { name: 'スチール板材 5mm', value: 675000, percentage: 27.6 },
      { name: '完成品 モーターケース', value: 212500, percentage: 8.7 },
      { name: 'アルミニウム角材', value: 60000, percentage: 2.4 },
      { name: '切削油 20L', value: 57600, percentage: 2.4 },
      { name: '電動ドリル替刃', value: 30000, percentage: 1.2 }
    ],
    byQuantity: [
      { name: 'ボルト M8x50', quantity: 120, percentage: 15.8 },
      { name: 'スチール板材 5mm', quantity: 45, percentage: 5.9 },
      { name: '電動ドリル替刃', quantity: 25, percentage: 3.3 },
      { name: '切削油 20L', quantity: 18, percentage: 2.4 },
      { name: 'アルミニウム角材', quantity: 12, percentage: 1.6 }
    ],
    byTurnover: [
      { name: 'ボルト M8x50', turnover: 4.2, status: 'excellent' },
      { name: '切削油 20L', turnover: 3.8, status: 'excellent' },
      { name: 'スチール板材 5mm', turnover: 3.1, status: 'good' },
      { name: '電動ドリル替刃', turnover: 2.9, status: 'good' },
      { name: 'アルミニウム角材', turnover: 1.8, status: 'warning' }
    ]
  }
};

export default function Reports() {
  const [reportType, setReportType] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('6months');

  return (
    <div className="reports">
      <div className="reports-header">
        <h2>データ分析・レポート</h2>
        <div className="report-controls">
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="filter-select"
          >
            <option value="overview">総合レポート</option>
            <option value="abc">ABC分析</option>
            <option value="turnover">在庫回転率</option>
            <option value="level">在庫レベル分析</option>
            <option value="health">在庫健全性</option>
            <option value="cost">コスト分析</option>
            <option value="trend">トレンド分析</option>
          </select>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="filter-select"
          >
            <option value="1month">直近1ヶ月</option>
            <option value="3months">直近3ヶ月</option>
            <option value="6months">直近6ヶ月</option>
            <option value="1year">直近1年</option>
          </select>
          <button className="generate-btn">CSV出力</button>
          <button className="export-btn">PDF出力</button>
        </div>
      </div>

      {reportType === 'overview' && (
        <div className="report-overview">
          {/* 在庫健全性スコア */}
          <div className="report-section health-score-section">
            <div className="section-header">
              <h3>在庫健全性スコア</h3>
              <span className="last-updated">最終更新: 2025年1月31日</span>
            </div>
            <div className="health-score-container">
              <div className="health-score-main">
                <div className="score-circle">
                  <svg viewBox="0 0 200 200" className="score-svg">
                    <circle cx="100" cy="100" r="80" fill="none" stroke="#ecf0f1" strokeWidth="20"></circle>
                    <circle 
                      cx="100" cy="100" r="80" fill="none" 
                      stroke="#3498db" strokeWidth="20"
                      strokeDasharray={`${(mockReportData.inventoryHealth.score / 100) * 502} 502`}
                      strokeLinecap="round"
                      transform="rotate(-90 100 100)"
                    ></circle>
                    <text x="100" y="95" textAnchor="middle" fontSize="36" fontWeight="700" fill="#2c3e50">
                      {mockReportData.inventoryHealth.score}
                    </text>
                    <text x="100" y="115" textAnchor="middle" fontSize="14" fill="#7f8c8d">点</text>
                  </svg>
                </div>
                <div className="score-details">
                  <div className="score-grade">
                    <span className="grade-label">総合評価</span>
                    <span className={`grade-value grade-${mockReportData.inventoryHealth.grade}`}>
                      {mockReportData.inventoryHealth.grade}
                    </span>
                  </div>
                  <div className="score-description">
                    在庫管理は良好な状態です。一部改善の余地がありますが、全体的に効率的に運用されています。
                  </div>
                </div>
              </div>
              <div className="health-factors">
                {mockReportData.inventoryHealth.factors.map((factor, index) => (
                  <div key={index} className={`health-factor ${factor.status}`}>
                    <div className="factor-header">
                      <span className="factor-name">{factor.name}</span>
                      <span className="factor-score">{factor.score}点</span>
                    </div>
                    <div className="factor-bar-wrapper">
                      <div 
                        className="factor-bar"
                        style={{ width: `${factor.score}%` }}
                      ></div>
                    </div>
                    <div className="factor-description">{factor.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* トップアイテム */}
          <div className="report-section top-items-section">
            <div className="section-header">
              <h3>🏆 トップアイテム分析</h3>
            </div>
            <div className="top-items-grid">
              <div className="top-items-card">
                <h4>在庫価値トップ5</h4>
                <div className="top-items-list">
                  {mockReportData.topItems.byValue.map((item, index) => (
                    <div key={index} className="top-item">
                      <div className="item-rank">{index + 1}</div>
                      <div className="item-info">
                        <div className="item-name">{item.name}</div>
                        <div className="item-bar-wrapper">
                          <div 
                            className="item-bar value-bar"
                            style={{ width: `${item.percentage * 3}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="item-value">
                        <div className="value-amount">¥{item.value.toLocaleString()}</div>
                        <div className="value-percentage">{item.percentage}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="top-items-card">
                <h4>在庫回転率トップ5</h4>
                <div className="top-items-list">
                  {mockReportData.topItems.byTurnover.map((item, index) => (
                    <div key={index} className="top-item">
                      <div className="item-rank">{index + 1}</div>
                      <div className="item-info">
                        <div className="item-name">{item.name}</div>
                        <div className="item-bar-wrapper">
                          <div 
                            className={`item-bar turnover-bar ${item.status}`}
                            style={{ width: `${(item.turnover / 4.5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="item-value">
                        <div className="value-amount">{item.turnover}回</div>
                        <div className={`value-status ${item.status}`}>
                          {item.status === 'excellent' ? '優秀' : item.status === 'good' ? '良好' : '要改善'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 推奨アクション */}
          <div className="report-section recommendations-section">
            <div className="section-header">
              <h3>推奨アクション</h3>
            </div>
            <div className="recommendations-grid">
              <div className="recommendation-card priority-high">
                <div className="rec-icon"></div>
                <div className="rec-content">
                  <div className="rec-title">在庫切れアイテムの補充</div>
                  <div className="rec-description">
                    3アイテムが在庫切れ状態です。早急に発注手続きを進めてください。
                  </div>
                  <button className="rec-action-btn">詳細を見る</button>
                </div>
              </div>
              <div className="recommendation-card priority-medium">
                <div className="rec-icon"></div>
                <div className="rec-content">
                  <div className="rec-title">低在庫アイテムの監視</div>
                  <div className="rec-description">
                    12アイテムが最低在庫を下回っています。発注タイミングを確認してください。
                  </div>
                  <button className="rec-action-btn">詳細を見る</button>
                </div>
              </div>
              <div className="recommendation-card priority-low">
                <div className="rec-icon"></div>
                <div className="rec-content">
                  <div className="rec-title">在庫回転率の改善</div>
                  <div className="rec-description">
                    一部アイテムの回転率が低下しています。需要予測の見直しを推奨します。
                  </div>
                  <button className="rec-action-btn">詳細を見る</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'abc' && (
        <div className="report-section">
          <div className="section-header">
            <h3>📊 ABC分析 (パレート分析)</h3>
            <p className="section-description">
              在庫価値の80-20ルールを視覚化し、重要アイテムの特定を支援します
            </p>
          </div>
          
          <div className="abc-analysis-container">
            {/* パレート図 */}
            <div className="pareto-chart-wrapper">
              <h4>パレート図</h4>
              <div className="pareto-chart">
                <svg viewBox="0 0 600 400" className="pareto-svg">
                  <defs>
                    <linearGradient id="classAGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#e53e3e" />
                      <stop offset="100%" stopColor="#c53030" />
                    </linearGradient>
                    <linearGradient id="classBGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#dd6b20" />
                      <stop offset="100%" stopColor="#c05621" />
                    </linearGradient>
                    <linearGradient id="classCGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#38a169" />
                      <stop offset="100%" stopColor="#2f855a" />
                    </linearGradient>
                  </defs>

                  {/* 軸 */}
                  <line x1="80" y1="50" x2="80" y2="320" stroke="#2c3e50" strokeWidth="2" />
                  <line x1="80" y1="320" x2="520" y2="320" stroke="#2c3e50" strokeWidth="2" />
                  <line x1="520" y1="50" x2="520" y2="320" stroke="#3498db" strokeWidth="2" />

                  {/* グリッドライン */}
                  <line x1="80" y1="266" x2="520" y2="266" stroke="#e1e8ed" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="80" y1="212" x2="520" y2="212" stroke="#e1e8ed" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="80" y1="158" x2="520" y2="158" stroke="#e1e8ed" strokeWidth="1" strokeDasharray="3,3" />
                  <line x1="80" y1="104" x2="520" y2="104" stroke="#e1e8ed" strokeWidth="1" strokeDasharray="3,3" />

                  {/* 80%ライン */}
                  <line x1="80" y1="104" x2="520" y2="104" stroke="#e74c3c" strokeWidth="2" strokeDasharray="5,5" />
                  <text x="530" y="108" fontSize="12" fill="#e74c3c" fontWeight="600">80%</text>

                  {/* バー */}
                  <rect x="120" y="187" width="100" height="133" fill="url(#classAGradient)" className="pareto-bar" />
                  <rect x="250" y="236" width="100" height="84" fill="url(#classBGradient)" className="pareto-bar" />
                  <rect x="380" y="294" width="100" height="26" fill="url(#classCGradient)" className="pareto-bar" />

                  {/* 累積線 */}
                  <path
                    d="M 170 187 L 300 131 L 430 104"
                    fill="none"
                    stroke="#3498db"
                    strokeWidth="3"
                    className="cumulative-line"
                  />

                  {/* データポイント */}
                  <circle cx="170" cy="187" r="6" fill="#3498db" stroke="white" strokeWidth="2" className="data-point" />
                  <circle cx="300" cy="131" r="6" fill="#3498db" stroke="white" strokeWidth="2" className="data-point" />
                  <circle cx="430" cy="104" r="6" fill="#3498db" stroke="white" strokeWidth="2" className="data-point" />

                  {/* ラベル */}
                  <text x="170" y="340" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2c3e50">クラス A</text>
                  <text x="300" y="340" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2c3e50">クラス B</text>
                  <text x="430" y="340" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2c3e50">クラス C</text>

                  {/* Y軸ラベル（左） */}
                  <text x="70" y="325" textAnchor="end" fontSize="12" fill="#2c3e50">0</text>
                  <text x="70" y="266" textAnchor="end" fontSize="12" fill="#2c3e50">500K</text>
                  <text x="70" y="212" textAnchor="end" fontSize="12" fill="#2c3e50">1M</text>
                  <text x="70" y="158" textAnchor="end" fontSize="12" fill="#2c3e50">1.5M</text>
                  <text x="70" y="104" textAnchor="end" fontSize="12" fill="#2c3e50">2M</text>

                  {/* Y軸ラベル（右） */}
                  <text x="530" y="325" fontSize="12" fill="#3498db">0%</text>
                  <text x="530" y="266" fontSize="12" fill="#3498db">20%</text>
                  <text x="530" y="212" fontSize="12" fill="#3498db">40%</text>
                  <text x="530" y="158" fontSize="12" fill="#3498db">60%</text>
                  <text x="530" y="54" fontSize="12" fill="#3498db">100%</text>

                  {/* バーの値ラベル */}
                  <text x="170" y="180" textAnchor="middle" fontSize="12" fontWeight="700" fill="white">¥1.7M</text>
                  <text x="300" y="229" textAnchor="middle" fontSize="12" fontWeight="700" fill="white">¥588K</text>
                  <text x="430" y="287" textAnchor="middle" fontSize="12" fontWeight="700" fill="white">¥147K</text>

                  {/* パーセンテージラベル */}
                  <text x="175" y="182" fontSize="11" fontWeight="600" fill="#3498db">70.1%</text>
                  <text x="305" y="126" fontSize="11" fontWeight="600" fill="#3498db">94.1%</text>
                  <text x="435" y="99" fontSize="11" fontWeight="600" fill="#3498db">100%</text>

                  {/* 軸タイトル */}
                  <text x="300" y="370" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2c3e50">分類</text>
                  <text x="40" y="185" textAnchor="middle" fontSize="14" fontWeight="600" fill="#2c3e50" transform="rotate(-90 40 185)">在庫価値 (¥)</text>
                  <text x="560" y="185" textAnchor="middle" fontSize="14" fontWeight="600" fill="#3498db" transform="rotate(90 560 185)">累積割合 (%)</text>
                </svg>
              </div>
            </div>

            {/* ABC詳細カード */}
            <div className="abc-details-section">
              <h4>クラス別詳細分析</h4>
              <div className="abc-cards">
                {mockReportData.abcAnalysis.map((item, index) => {
                  const cumulative = index === 0 ? 70.1 : index === 1 ? 94.1 : 100;
                  return (
                    <div key={item.category} className={`abc-card abc-${item.category.toLowerCase()}`}>
                      <div className="abc-card-header">
                        <span className="abc-class">クラス {item.category}</span>
                        <span className="abc-badge">{item.description}</span>
                      </div>
                      <div className="abc-card-body">
                        <div className="abc-stat-row">
                          <div className="abc-stat">
                            <span className="stat-label">アイテム数</span>
                            <span className="stat-value">{item.items}点</span>
                            <span className="stat-percentage">({item.percentage}%)</span>
                          </div>
                          <div className="abc-stat">
                            <span className="stat-label">在庫価値</span>
                            <span className="stat-value">¥{item.value.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="abc-cumulative">
                          <span className="cumulative-label">累積寄与率</span>
                          <div className="cumulative-bar-wrapper">
                            <div className="cumulative-bar" style={{ width: `${cumulative}%` }}></div>
                          </div>
                          <span className="cumulative-value">{cumulative}%</span>
                        </div>
                      </div>
                      <div className="abc-card-footer">
                        <button className="abc-detail-btn">詳細アイテムを表示</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="abc-insights">
                <h4>💡 分析インサイト</h4>
                <ul className="insights-list">
                  <li>
                    <strong>クラスA</strong>は全アイテムの15.6%を占めるが、在庫価値の70.1%を占めています。
                    これらのアイテムの在庫管理を最優先すべきです。
                  </li>
                  <li>
                    <strong>クラスB</strong>は中程度の価値を持つアイテムで、定期的な監視が推奨されます。
                  </li>
                  <li>
                    <strong>クラスC</strong>は多数のアイテムを含みますが価値は低いため、
                    効率的な管理方法（例：定期一括発注）を検討してください。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'turnover' && (
        <div className="report-section">
          <div className="section-header">
            <h3>🔄 在庫回転率分析</h3>
            <p className="section-description">
              月別の在庫回転率を表示し、在庫効率の改善点を特定します
            </p>
          </div>
          
          <div className="turnover-analysis-container">
            <div className="turnover-stats-summary">
              <div className="turnover-stat-card">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <div className="stat-label">平均回転率</div>
                  <div className="stat-value">3.0回/月</div>
                  <div className="stat-change positive">目標達成</div>
                </div>
              </div>
              <div className="turnover-stat-card">
                <div className="stat-icon">✓</div>
                <div className="stat-content">
                  <div className="stat-label">目標達成月</div>
                  <div className="stat-value">3ヶ月</div>
                  <div className="stat-change">過去6ヶ月中</div>
                </div>
              </div>
              <div className="turnover-stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <div className="stat-label">最高回転率</div>
                  <div className="stat-value">3.4回</div>
                  <div className="stat-change">2024年9月</div>
                </div>
              </div>
            </div>

            <div className="turnover-chart-wrapper">
              <h4>月別在庫回転率推移</h4>
              <div className="turnover-chart">
                <div className="chart-y-axis">
                  <span>4.0</span>
                  <span>3.5</span>
                  <span className="target-label">3.0 (目標)</span>
                  <span>2.5</span>
                  <span>2.0</span>
                  <span>1.5</span>
                </div>
                <div className="chart-area">
                  <div className="chart-grid">
                    {[4.0, 3.5, 3.0, 2.5, 2.0, 1.5].map((value, i) => (
                      <div key={i} className={`grid-line ${value === 3.0 ? 'target-line' : ''}`}></div>
                    ))}
                  </div>
                  <div className="chart-bars">
                    {mockReportData.turnoverRate.map((item, index) => (
                      <div key={index} className="bar-group">
                        <div className="bar-wrapper">
                          <div 
                            className={`turnover-bar ${item.status}`}
                            style={{ height: `${((item.rate - 1.5) / 2.5) * 100}%` }}
                          >
                            <span className="bar-value">{item.rate}</span>
                          </div>
                        </div>
                        <div className="bar-label">{item.period.replace('2024年', '').replace('月', '月')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="turnover-legend">
                <div className="legend-item">
                  <div className="legend-color above"></div>
                  <span>目標達成 (≥3.0)</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color below"></div>
                  <span>要改善 (&lt;3.0)</span>
                </div>
              </div>
            </div>

            <div className="turnover-insights">
              <h4>💡 改善提案</h4>
              <div className="insights-cards">
                <div className="insight-card">
                  <div className="insight-header">
                    <span className="insight-icon">📉</span>
                    <span className="insight-title">回転率が低下している月</span>
                  </div>
                  <div className="insight-body">
                    7月、10月、12月の回転率が目標を下回っています。
                    これらの月の需要パターンを分析し、在庫計画を最適化してください。
                  </div>
                </div>
                <div className="insight-card">
                  <div className="insight-header">
                    <span className="insight-icon">🎯</span>
                    <span className="insight-title">ベストプラクティス</span>
                  </div>
                  <div className="insight-body">
                    9月は3.4回転と最高のパフォーマンスを記録しました。
                    この月の発注・販売戦略を他の月にも適用できないか検討してください。
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'level' && (
        <div className="report-section">
          <div className="section-header">
            <h3>📈 在庫レベル分析</h3>
            <p className="section-description">
              カテゴリ別の在庫レベルを分析し、最適在庫との比較を表示します
            </p>
          </div>
          
          <div className="level-analysis-container">
            <div className="level-bars-section">
              <h4>カテゴリ別在庫充足率</h4>
              <div className="level-bars">
                {mockReportData.stockLevel.map((item, index) => (
                  <div key={index} className="level-bar-item">
                    <div className="level-bar-header">
                      <span className="category-name">{item.category}</span>
                      <span className="category-stats">
                        <span className="current-value">{item.current}</span>
                        <span className="separator">/</span>
                        <span className="optimal-value">{item.optimal}</span>
                      </span>
                      <span className="category-percentage">{item.percentage.toFixed(1)}%</span>
                    </div>
                    <div className="level-bar-wrapper">
                      <div 
                        className={`level-bar ${
                          item.percentage >= 90 ? 'excellent' : 
                          item.percentage >= 80 ? 'good' : 
                          item.percentage >= 70 ? 'warning' : 'danger'
                        }`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                      <div className="optimal-marker" style={{ left: '90%' }}></div>
                    </div>
                    <div className="level-bar-footer">
                      <span className={`status-label ${
                        item.percentage >= 90 ? 'excellent' : 
                        item.percentage >= 80 ? 'good' : 
                        item.percentage >= 70 ? 'warning' : 'danger'
                      }`}>
                        {item.percentage >= 90 ? '✓ 優良' : 
                         item.percentage >= 80 ? '○ 良好' : 
                         item.percentage >= 70 ? '△ 注意' : '✕ 要改善'}
                      </span>
                      {item.percentage < 90 && (
                        <span className="shortage-amount">
                          {item.optimal - item.current}個不足
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="level-summary-section">
              <h4>在庫状況サマリー</h4>
              <div className="level-summary-cards">
                <div className="summary-stat excellent">
                  <div className="stat-icon">✓</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {mockReportData.stockLevel.filter(i => i.percentage >= 90).length}
                    </div>
                    <div className="stat-label">優良カテゴリ</div>
                    <div className="stat-sublabel">≥90%</div>
                  </div>
                </div>
                <div className="summary-stat good">
                  <div className="stat-icon">○</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {mockReportData.stockLevel.filter(i => i.percentage >= 80 && i.percentage < 90).length}
                    </div>
                    <div className="stat-label">良好カテゴリ</div>
                    <div className="stat-sublabel">80-89%</div>
                  </div>
                </div>
                <div className="summary-stat warning">
                  <div className="stat-icon">△</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {mockReportData.stockLevel.filter(i => i.percentage >= 70 && i.percentage < 80).length}
                    </div>
                    <div className="stat-label">注意カテゴリ</div>
                    <div className="stat-sublabel">70-79%</div>
                  </div>
                </div>
                <div className="summary-stat danger">
                  <div className="stat-icon">✕</div>
                  <div className="stat-content">
                    <div className="stat-number">
                      {mockReportData.stockLevel.filter(i => i.percentage < 70).length}
                    </div>
                    <div className="stat-label">改善必要</div>
                    <div className="stat-sublabel">&lt;70%</div>
                  </div>
                </div>
              </div>
              
              <div className="level-recommendations">
                <h5>📋 推奨発注リスト</h5>
                <div className="recommendations-table">
                  {mockReportData.stockLevel
                    .filter(item => item.percentage < 90)
                    .map((item, index) => (
                      <div key={index} className="recommendation-row">
                        <div className="rec-category">{item.category}</div>
                        <div className="rec-details">
                          <span className="rec-shortage">不足: {item.optimal - item.current}個</span>
                          <span className="rec-priority">
                            {item.percentage < 70 ? '優先度: 高' : 
                             item.percentage < 80 ? '優先度: 中' : '優先度: 低'}
                          </span>
                        </div>
                        <button className="rec-order-btn">発注手続き</button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'health' && (
        <div className="report-section">
          <div className="section-header">
            <h3>🏥 在庫健全性レポート</h3>
            <p className="section-description">
              在庫管理の健全性を多角的に評価し、改善点を特定します
            </p>
          </div>
          
          <div className="health-analysis-container">
            <div className="health-main-score">
              <div className="score-display">
                <div className="score-circle-large">
                  <svg viewBox="0 0 240 240" className="score-svg-large">
                    <circle cx="120" cy="120" r="100" fill="none" stroke="#ecf0f1" strokeWidth="24"></circle>
                    <circle 
                      cx="120" cy="120" r="100" fill="none" 
                      stroke={
                        mockReportData.inventoryHealth.score >= 85 ? '#27ae60' :
                        mockReportData.inventoryHealth.score >= 70 ? '#3498db' :
                        mockReportData.inventoryHealth.score >= 55 ? '#f39c12' : '#e74c3c'
                      }
                      strokeWidth="24"
                      strokeDasharray={`${(mockReportData.inventoryHealth.score / 100) * 628} 628`}
                      strokeLinecap="round"
                      transform="rotate(-90 120 120)"
                    ></circle>
                    <text x="120" y="110" textAnchor="middle" fontSize="48" fontWeight="700" fill="#2c3e50">
                      {mockReportData.inventoryHealth.score}
                    </text>
                    <text x="120" y="140" textAnchor="middle" fontSize="18" fill="#7f8c8d">点 / 100点</text>
                  </svg>
                </div>
                <div className="score-grade-display">
                  <div className="grade-title">総合評価</div>
                  <div className={`grade-badge grade-${mockReportData.inventoryHealth.grade}`}>
                    {mockReportData.inventoryHealth.grade}
                  </div>
                  <div className="grade-description">
                    {mockReportData.inventoryHealth.score >= 85 ? '優秀 - 在庫管理が非常に良好です' :
                     mockReportData.inventoryHealth.score >= 70 ? '良好 - 適切に管理されています' :
                     mockReportData.inventoryHealth.score >= 55 ? '要注意 - 改善の余地があります' :
                     '要改善 - 早急な対策が必要です'}
                  </div>
                </div>
              </div>
            </div>

            <div className="health-factors-detailed">
              <h4>評価項目別スコア</h4>
              <div className="factors-grid">
                {mockReportData.inventoryHealth.factors.map((factor, index) => (
                  <div key={index} className={`factor-card ${factor.status}`}>
                    <div className="factor-card-header">
                      <span className="factor-name">{factor.name}</span>
                      <span className="factor-score-badge">{factor.score}点</span>
                    </div>
                    <div className="factor-progress-bar">
                      <div 
                        className="factor-progress"
                        style={{ width: `${factor.score}%` }}
                      ></div>
                    </div>
                    <div className="factor-description">{factor.description}</div>
                    <div className="factor-status-badge">
                      {factor.status === 'excellent' ? '✓ 優秀' :
                       factor.status === 'good' ? '○ 良好' :
                       factor.status === 'warning' ? '△ 注意' : '✕ 要改善'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="health-timeline">
              <h4>健全性スコア推移</h4>
              <div className="timeline-chart">
                <div className="timeline-bars">
                  {[75, 76, 78, 77, 79, 78.5].map((score, index) => (
                    <div key={index} className="timeline-bar-group">
                      <div className="timeline-bar-wrapper">
                        <div 
                          className={`timeline-bar ${
                            score >= 85 ? 'excellent' :
                            score >= 70 ? 'good' :
                            score >= 55 ? 'warning' : 'danger'
                          }`}
                          style={{ height: `${score}%` }}
                        >
                          <span className="bar-value">{score}</span>
                        </div>
                      </div>
                      <div className="timeline-label">{7 + index}月</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'cost' && (
        <div className="report-section">
          <div className="section-header">
            <h3>💰 コスト分析</h3>
            <p className="section-description">
              在庫コストと利益率を分析し、収益性を最適化します
            </p>
          </div>
          
          <div className="cost-analysis-container">
            <div className="cost-summary-cards">
              <div className="cost-card total-cost">
                <div className="cost-icon">💵</div>
                <div className="cost-content">
                  <div className="cost-label">総在庫原価</div>
                  <div className="cost-value">¥{mockReportData.costAnalysis.totalCost.toLocaleString()}</div>
                </div>
              </div>
              <div className="cost-card total-value">
                <div className="cost-icon">💰</div>
                <div className="cost-content">
                  <div className="cost-label">総在庫売価</div>
                  <div className="cost-value">¥{mockReportData.costAnalysis.totalValue.toLocaleString()}</div>
                </div>
              </div>
              <div className="cost-card margin">
                <div className="cost-icon">📊</div>
                <div className="cost-content">
                  <div className="cost-label">平均利益率</div>
                  <div className="cost-value">{mockReportData.costAnalysis.averageMargin}%</div>
                </div>
              </div>
            </div>

            <div className="cost-by-category">
              <h4>カテゴリ別コスト分析</h4>
              <div className="cost-table">
                <div className="cost-table-header">
                  <div className="col-category">カテゴリ</div>
                  <div className="col-items">アイテム数</div>
                  <div className="col-cost">在庫原価</div>
                  <div className="col-value">在庫売価</div>
                  <div className="col-margin">利益率</div>
                  <div className="col-chart">分布</div>
                </div>
                {mockReportData.costAnalysis.categories.map((cat, index) => (
                  <div key={index} className="cost-table-row">
                    <div className="col-category">{cat.name}</div>
                    <div className="col-items">{cat.items}点</div>
                    <div className="col-cost">¥{cat.cost.toLocaleString()}</div>
                    <div className="col-value">¥{cat.value.toLocaleString()}</div>
                    <div className="col-margin">
                      <span className={`margin-badge ${
                        cat.margin >= 25 ? 'excellent' :
                        cat.margin >= 20 ? 'good' : 'warning'
                      }`}>
                        {cat.margin}%
                      </span>
                    </div>
                    <div className="col-chart">
                      <div className="mini-bar-chart">
                        <div 
                          className="mini-bar cost-bar"
                          style={{ width: `${(cat.cost / mockReportData.costAnalysis.totalCost) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cost-insights">
              <h4>💡 コスト最適化提案</h4>
              <div className="cost-insights-grid">
                <div className="insight-card">
                  <div className="insight-icon">📈</div>
                  <div className="insight-content">
                    <div className="insight-title">高利益率カテゴリ</div>
                    <div className="insight-text">
                      「部品」カテゴリが最高の利益率（25.0%）を記録しています。
                      このカテゴリの在庫を増やすことで収益性が向上する可能性があります。
                    </div>
                  </div>
                </div>
                <div className="insight-card">
                  <div className="insight-icon">💡</div>
                  <div className="insight-content">
                    <div className="insight-title">コスト削減機会</div>
                    <div className="insight-text">
                      「原材料」カテゴリが総コストの43%を占めています。
                      サプライヤーとの価格交渉やまとめ買いでコスト削減が期待できます。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reportType === 'trend' && (
        <div className="report-section">
          <div className="section-header">
            <h3>📉 トレンド分析</h3>
            <p className="section-description">
              過去6ヶ月間の在庫トレンドを分析し、将来の予測を支援します
            </p>
          </div>
          
          <div className="trend-analysis-container">
            <div className="trend-chart-section">
              <h4>在庫価値推移</h4>
              <div className="trend-line-chart">
                <svg viewBox="0 0 600 300" className="trend-svg">
                  <defs>
                    <linearGradient id="trendGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(52, 152, 219, 0.3)" />
                      <stop offset="100%" stopColor="rgba(52, 152, 219, 0.05)" />
                    </linearGradient>
                  </defs>
                  
                  {/* グリッド */}
                  {[0, 1, 2, 3, 4].map((i) => (
                    <line 
                      key={i}
                      x1="50" y1={50 + i * 50} 
                      x2="550" y2={50 + i * 50}
                      stroke="#e1e8ed" strokeWidth="1" strokeDasharray="3,3"
                    />
                  ))}
                  
                  {/* 軸 */}
                  <line x1="50" y1="50" x2="50" y2="250" stroke="#2c3e50" strokeWidth="2" />
                  <line x1="50" y1="250" x2="550" y2="250" stroke="#2c3e50" strokeWidth="2" />
                  
                  {/* データライン */}
                  <path
                    d="M 80 220 L 170 200 L 260 180 L 350 190 L 440 185 L 530 175"
                    fill="url(#trendGradient)"
                    stroke="#3498db"
                    strokeWidth="3"
                  />
                  <path
                    d="M 80 220 L 170 200 L 260 180 L 350 190 L 440 185 L 530 175 L 530 250 L 80 250 Z"
                    fill="url(#trendGradient)"
                  />
                  
                  {/* データポイント */}
                  {mockReportData.trendAnalysis.map((item, index) => {
                    const x = 80 + index * 90;
                    const y = 250 - ((item.totalValue - 2200000) / 300000) * 200;
                    return (
                      <g key={index}>
                        <circle cx={x} cy={y} r="6" fill="#3498db" stroke="white" strokeWidth="2" />
                        <text x={x} y={y - 15} textAnchor="middle" fontSize="11" fill="#2c3e50" fontWeight="600">
                          ¥{(item.totalValue / 1000000).toFixed(2)}M
                        </text>
                      </g>
                    );
                  })}
                  
                  {/* X軸ラベル */}
                  {mockReportData.trendAnalysis.map((item, index) => {
                    const x = 80 + index * 90;
                    return (
                      <text key={index} x={x} y="270" textAnchor="middle" fontSize="12" fill="#7f8c8d">
                        {item.month.substring(5)}月
                      </text>
                    );
                  })}
                  
                  {/* Y軸ラベル */}
                  <text x="40" y="255" textAnchor="end" fontSize="11" fill="#7f8c8d">2.2M</text>
                  <text x="40" y="205" textAnchor="end" fontSize="11" fill="#7f8c8d">2.3M</text>
                  <text x="40" y="155" textAnchor="end" fontSize="11" fill="#7f8c8d">2.4M</text>
                  <text x="40" y="105" textAnchor="end" fontSize="11" fill="#7f8c8d">2.5M</text>
                  <text x="40" y="55" textAnchor="end" fontSize="11" fill="#7f8c8d">2.6M</text>
                </svg>
              </div>
            </div>

            <div className="trend-metrics-grid">
              <div className="trend-metric-card">
                <h5>在庫アイテム数推移</h5>
                <div className="metric-mini-chart">
                  {mockReportData.trendAnalysis.map((item, index) => (
                    <div key={index} className="mini-bar">
                      <div 
                        className="mini-bar-fill"
                        style={{ height: `${(item.totalItems / 150) * 100}%` }}
                      ></div>
                      <span className="mini-bar-label">{item.month.substring(5)}</span>
                    </div>
                  ))}
                </div>
                <div className="metric-summary">
                  平均: 145.8アイテム
                </div>
              </div>

              <div className="trend-metric-card">
                <h5>低在庫アイテム数推移</h5>
                <div className="metric-mini-chart warning">
                  {mockReportData.trendAnalysis.map((item, index) => (
                    <div key={index} className="mini-bar">
                      <div 
                        className="mini-bar-fill"
                        style={{ height: `${(item.lowStock / 15) * 100}%` }}
                      ></div>
                      <span className="mini-bar-label">{item.month.substring(5)}</span>
                    </div>
                  ))}
                </div>
                <div className="metric-summary">
                  平均: 10.3アイテム
                </div>
              </div>

              <div className="trend-metric-card">
                <h5>在庫切れアイテム数推移</h5>
                <div className="metric-mini-chart danger">
                  {mockReportData.trendAnalysis.map((item, index) => (
                    <div key={index} className="mini-bar">
                      <div 
                        className="mini-bar-fill"
                        style={{ height: `${(item.outOfStock / 5) * 100}%` }}
                      ></div>
                      <span className="mini-bar-label">{item.month.substring(5)}</span>
                    </div>
                  ))}
                </div>
                <div className="metric-summary">
                  平均: 2.3アイテム
                </div>
              </div>
            </div>

            <div className="trend-forecast">
              <h4>📊 予測と推奨</h4>
              <div className="forecast-cards">
                <div className="forecast-card positive">
                  <div className="forecast-icon">📈</div>
                  <div className="forecast-content">
                    <div className="forecast-title">在庫価値の増加傾向</div>
                    <div className="forecast-text">
                      過去6ヶ月で在庫価値が7.5%増加しています。
                      現在のトレンドが続けば、来月は約¥2,480,000に達する見込みです。
                    </div>
                  </div>
                </div>
                <div className="forecast-card warning">
                  <div className="forecast-icon">⚠️</div>
                  <div className="forecast-content">
                    <div className="forecast-title">低在庫アイテムの増加</div>
                    <div className="forecast-text">
                      低在庫アイテム数が増加傾向にあります。
                      発注サイクルの見直しや安全在庫の再設定を検討してください。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
